import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  PanResponder,
  Platform,
  Text,
  TouchableOpacity,
  View,
  ViewToken,
} from "react-native";
import styles from "./styles";
import PostSingle, {
  MediaPreloadMode,
  PostSingleHandles,
} from "../../components/general/post";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStore } from "react-redux";
import {
  columnVideosToPosts,
  getFeed,
  getPostsByUserId,
  type MobileFeedSort,
} from "../../services/posts";
import { Post } from "../../../types";
import {
  NavigationProp,
  ParamListBase,
  RouteProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import { RootStackParamList } from "../../navigation/main";
import { HomeStackParamList } from "../../navigation/home";
import {
  CurrentUserProfileItemInViewContext,
  FeedStackParamList,
} from "../../navigation/feed";
import { RootState } from "../../redux/store";
import { LoopMode } from "../../redux/slices/playbackSlice";
import { Feather } from "@expo/vector-icons";
import { navigateToHomeFeed } from "../../utils/navigationFallback";
import {
  DEFAULT_SMART_PLAYBACK_POLICY,
  fetchSmartPlaybackPolicy,
  type SmartPlaybackPolicy,
} from "../../services/videoServer";

type FeedScreenRouteProp =
  | RouteProp<RootStackParamList, "userPosts">
  | RouteProp<RootStackParamList, "columnVideo">
  | RouteProp<HomeStackParamList, "feed">
  | RouteProp<FeedStackParamList, "feedList">;

interface PostViewToken extends ViewToken {
  item: Post;
}

type MobileFeedStrategyRoute = {
  key: MobileFeedSort;
  group: "用户策略" | "综合策略";
  label: string;
};

const mobileFeedStrategies: MobileFeedStrategyRoute[] = [
  { key: "personalized", group: "用户策略", label: "智能推荐" },
  { key: "favorites", group: "用户策略", label: "用户收藏" },
  { key: "public", group: "综合策略", label: "分数最高" },
];

function sharedVideoIdFromLocation() {
  if (Platform.OS !== "web" || typeof window === "undefined") return "";
  return new URLSearchParams(window.location.search).get("video") || "";
}

function getSequenceIndex(posts: Post[], currentIndex: number) {
  return posts.length > 0 ? (currentIndex + 1) % posts.length : 0;
}

function mobilePostTerms(post: Post) {
  return new Set(
    [
      post.description,
      post.channel || "",
      ...(post.tags || []),
      ...(post.adKeywords || []),
    ]
      .join(" ")
      .toLocaleLowerCase()
      .split(/[\s,._\-，。:：/|#]+/)
      .filter((term) => term.length >= 2),
  );
}

function getSmartIndex(
  posts: Post[],
  currentIndex: number,
  policy: SmartPlaybackPolicy,
  recentPostIds: string[] = [],
) {
  const current = posts[currentIndex];
  if (!current || posts.length < 2) {
    return currentIndex;
  }

  const recentIds = new Set(
    policy.recentHistorySize > 0
      ? recentPostIds.slice(-policy.recentHistorySize)
      : [],
  );
  const allCandidates = posts
    .map((post, index) => ({ post, index }))
    .filter(({ index }) => index !== currentIndex);
  const nonRecentCandidates = allCandidates.filter(
    ({ post }) => !recentIds.has(post.id),
  );
  const pool =
    nonRecentCandidates.length > 0 ? nonRecentCandidates : allCandidates;
  if (
    pool.length > 1 &&
    Math.random() * 100 < policy.explorationPercent
  ) {
    return pool[Math.floor(Math.random() * pool.length)].index;
  }
  const currentTokens = mobilePostTerms(current);
  let bestIndex = getSequenceIndex(posts, currentIndex);
  let bestScore = Number.NEGATIVE_INFINITY;

  pool.forEach(({ post, index }) => {
    const sharedTerms = [...mobilePostTerms(post)].filter((token) =>
      currentTokens.has(token),
    ).length;
    const score =
      sharedTerms * (policy.relatedTermsWeight / 10) +
      (post.isAdvertisement
        ? sharedTerms * (policy.advertisementRelevanceWeight / 10)
        : 0) +
      (post.channel === current.channel
        ? policy.sameChannelWeight / 10
        : 0) +
      (post.category === current.category
        ? policy.sameCategoryWeight / 10
        : 0) +
      ((post.ranking?.personalizedScore || 0) / 100) *
        policy.personalizedWeight +
      ((post.ranking?.publicScore || post.rankingScore || 0) / 100) *
        policy.publicWeight;

    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestIndex;
}

function getNextIndex(
  posts: Post[],
  currentIndex: number,
  loopMode: LoopMode,
  policy: SmartPlaybackPolicy,
  recentPostIds: string[],
) {
  if (posts.length < 2) {
    return currentIndex;
  }

  if (loopMode === "shuffle") {
    let nextIndex = currentIndex;
    while (nextIndex === currentIndex) {
      nextIndex = Math.floor(Math.random() * posts.length);
    }
    return nextIndex;
  }

  if (loopMode === "smart") {
    return getSmartIndex(posts, currentIndex, policy, recentPostIds);
  }

  return getSequenceIndex(posts, currentIndex);
}

/**
 * Component that renders a list of posts meant to be
 * used for the feed screen.
 *
 * On start make fetch for posts then use a flatList
 * to display/control the posts.
 */
function FeedList({
  route,
  feedSort = "personalized",
  feedActive = true,
  onHorizontalSwipe,
  onChromeVisibilityChange,
}: {
  route: FeedScreenRouteProp;
  feedSort?: MobileFeedSort;
  feedActive?: boolean;
  onHorizontalSwipe?: (direction: "left" | "right") => void;
  onChromeVisibilityChange?: (visible: boolean) => void;
}) {
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const { setCurrentUserProfileItemInView } = useContext(
    CurrentUserProfileItemInViewContext,
  );

  const { creator, profile } = route.params as {
    creator: string;
    profile: boolean;
  };
  const columnParams = route.params as
    | RootStackParamList["columnVideo"]
    | undefined;

  const [posts, setPosts] = useState<Post[]>([]);
  const [feedItemHeight, setFeedItemHeight] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const mediaRefs = useRef<Record<string, PostSingleHandles | null>>({});
  const flatListRef = useRef<FlatList<Post>>(null);
  const postsRef = useRef<Post[]>([]);
  const playedIndexesRef = useRef<Set<number>>(new Set());
  const recentPostIdsRef = useRef<string[]>([]);
  const smartPlaybackPolicyRef = useRef<SmartPlaybackPolicy>(
    DEFAULT_SMART_PLAYBACK_POLICY,
  );
  const store = useStore<RootState>();

  useEffect(() => {
    postsRef.current = posts;
  }, [posts]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const playOnlyIndex = useCallback((index: number) => {
    const currentPost = postsRef.current[index];
    Object.entries(mediaRefs.current).forEach(([postId, cell]) => {
      if (!cell) {
        return;
      }
      if (currentPost?.id === postId) {
        cell.play();
      } else {
        cell.stop();
      }
    });
  }, []);

  useEffect(() => {
    if (feedActive) {
      playOnlyIndex(currentIndex);
      return;
    }

    Object.values(mediaRefs.current).forEach((cell) => cell?.stop());
  }, [currentIndex, feedActive, playOnlyIndex, posts]);

  useFocusEffect(
    useCallback(() => {
      let active = true;

      if (!feedActive) {
        Object.values(mediaRefs.current).forEach((cell) => cell?.stop());
        return () => {
          active = false;
        };
      }

      const loadPosts = () =>
        columnParams?.videos
          ? Promise.resolve(columnVideosToPosts(columnParams.videos))
          : profile && creator
          ? getPostsByUserId(creator)
          : getFeed(feedSort);

      void fetchSmartPlaybackPolicy()
        .then((result) => {
          if (active) smartPlaybackPolicyRef.current = result.policy;
        })
        .catch(() => {
          if (active) {
            smartPlaybackPolicyRef.current = DEFAULT_SMART_PLAYBACK_POLICY;
          }
        });

      const applyPosts = (nextPosts: Post[], resetPosition: boolean) => {
        if (!active) {
          return;
        }

        postsRef.current = nextPosts;
        setPosts(nextPosts);
        if (resetPosition) {
          const requestedVideoId = sharedVideoIdFromLocation();
          const requestedVideoIndex = requestedVideoId
            ? nextPosts.findIndex(
                (post) => post.videoServerId === requestedVideoId,
              )
            : -1;
          const initialIndex =
            requestedVideoIndex >= 0
              ? requestedVideoIndex
              : columnParams?.initialIndex ?? 0;
          playedIndexesRef.current.clear();
          recentPostIdsRef.current = [];
          setCurrentIndex(initialIndex);
          setTimeout(() => {
            flatListRef.current?.scrollToIndex({
              index: initialIndex,
              animated: false,
            });
          }, 100);
          return;
        }

        setCurrentIndex((index) =>
          Math.min(index, Math.max(0, nextPosts.length - 1)),
        );
      };

      loadPosts()
        .then((nextPosts) => {
          if (active) {
            applyPosts(nextPosts, true);
          }
        })
        .catch((error) => {
          console.warn("Failed to load feed:", error);
        });

      const refreshTimer =
        !columnParams?.videos && !profile
          ? setInterval(() => {
              loadPosts()
                .then((nextPosts) => applyPosts(nextPosts, false))
                .catch((error) => {
                  console.warn("Failed to refresh feed:", error);
                });
            }, 15000)
          : null;

      return () => {
        active = false;
        if (refreshTimer) {
          clearInterval(refreshTimer);
        }
      };
    }, [
      columnParams?.initialIndex,
      columnParams?.videos,
      creator,
      feedActive,
      feedSort,
      profile,
    ]),
  );

  /**
   * Called any time a new post is shown when a user scrolls
   * the FlatList, when this happens we should start playing
   * the post that is viewable and stop all the others
   */
  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: PostViewToken[] }) => {
      const visibleItem = viewableItems.find((item) => item.isViewable);
      if (!visibleItem) {
        return;
      }

      const index = postsRef.current.findIndex(
        (post) => post.id === visibleItem.item.id,
      );
      if (index < 0) {
        return;
      }

      playedIndexesRef.current.add(index);
      recentPostIdsRef.current.push(visibleItem.item.id);
      recentPostIdsRef.current = recentPostIdsRef.current.slice(-50);
      setCurrentIndex(index);
      if (!profile && setCurrentUserProfileItemInView) {
        setCurrentUserProfileItemInView(visibleItem.item.creator);
      }
    },
  );

  const handlePlaybackFinish = useCallback(
    (index: number) => {
      const currentLoopMode = store.getState().playback.loopMode;
      if (currentLoopMode === "single") {
        return;
      }

      const nextIndex = getNextIndex(
        postsRef.current,
        index,
        currentLoopMode,
        smartPlaybackPolicyRef.current,
        recentPostIdsRef.current,
      );
      setCurrentIndex(nextIndex);
      flatListRef.current?.scrollToIndex({
        index: nextIndex,
        animated: true,
      });
    },
    [store],
  );

  const updateIndexFromScrollOffset = useCallback(
    (offsetY: number) => {
      if (feedItemHeight <= 0) {
        return;
      }
      const nextIndex = Math.round(offsetY / feedItemHeight);
      const nextPost = postsRef.current[nextIndex];
      if (!nextPost) {
        return;
      }
      playedIndexesRef.current.add(nextIndex);
      setCurrentIndex((previousIndex) =>
        previousIndex === nextIndex ? previousIndex : nextIndex,
      );
      if (!profile && setCurrentUserProfileItemInView) {
        setCurrentUserProfileItemInView(nextPost.creator);
      }
    },
    [feedItemHeight, profile, setCurrentUserProfileItemInView],
  );

  const settleVerticalSwipe = useCallback((direction: "up" | "down") => {
    const maximumIndex = postsRef.current.length - 1;
    if (maximumIndex < 0) return;
    const nextIndex = Math.max(
      0,
      Math.min(
        maximumIndex,
        currentIndexRef.current + (direction === "up" ? 1 : -1),
      ),
    );
    if (nextIndex === currentIndexRef.current) return;
    currentIndexRef.current = nextIndex;
    playedIndexesRef.current.add(nextIndex);
    setCurrentIndex(nextIndex);
    flatListRef.current?.scrollToIndex({ index: nextIndex, animated: true });
  }, []);

  const completeDragGesture = useCallback(
    (dx: number, dy: number) => {
      const horizontalDistance = Math.abs(dx);
      const verticalDistance = Math.abs(dy);
      if (
        onHorizontalSwipe &&
        horizontalDistance >= 42 &&
        horizontalDistance > verticalDistance * 1.2
      ) {
        onHorizontalSwipe(dx < 0 ? "left" : "right");
        return;
      }
      if (
        verticalDistance >= 42 &&
        verticalDistance > horizontalDistance * 1.2
      ) {
        settleVerticalSwipe(dy < 0 ? "up" : "down");
      }
    },
    [onHorizontalSwipe, settleVerticalSwipe],
  );

  const gestureResponder = useMemo(() => {
    const shouldClaimGesture = (
      _event: unknown,
      gesture: { dx: number; dy: number },
    ) => {
      const horizontalDistance = Math.abs(gesture.dx);
      const verticalDistance = Math.abs(gesture.dy);
      if (Math.max(horizontalDistance, verticalDistance) < 12) return false;
      if (onHorizontalSwipe && horizontalDistance > verticalDistance * 1.2) {
        return true;
      }
      return verticalDistance > horizontalDistance * 1.2;
    };

    return PanResponder.create({
      onStartShouldSetPanResponder: () => false,
      onMoveShouldSetPanResponder: shouldClaimGesture,
      onMoveShouldSetPanResponderCapture: shouldClaimGesture,
      onPanResponderTerminationRequest: () => false,
      onPanResponderRelease: (_event, gesture) => {
        completeDragGesture(gesture.dx, gesture.dy);
      },
      onShouldBlockNativeResponder: () => true,
    });
  }, [completeDragGesture, onHorizontalSwipe]);

  /**
   * renders the item shown in the FlatList
   *
   * @param {Object} item object of the post
   * @param {Integer} index position of the post in the FlatList
   * @returns
   */
  const renderItem = ({ item, index }: { item: Post; index: number }) => {
    const itemCount = posts.length;
    const nextIndex = itemCount ? (currentIndex + 1) % itemCount : -1;
    const previousIndex = itemCount
      ? (currentIndex - 1 + itemCount) % itemCount
      : -1;
    const preloadMode: MediaPreloadMode =
      index === currentIndex
        ? "active"
        : index === nextIndex
        ? "next"
        : index === previousIndex
        ? "previous"
        : "none";
    return (
      <View
        style={{
          height: feedItemHeight,
          backgroundColor: "black",
        }}
      >
        <PostSingle
          item={item}
          isActive={feedActive && index === currentIndex}
          preloadMode={preloadMode}
          onPlaybackFinish={() => handlePlaybackFinish(index)}
          onWebDragComplete={completeDragGesture}
          onChromeVisibilityChange={onChromeVisibilityChange}
          strategyHeaderEnabled={Boolean(onHorizontalSwipe)}
          onFavoriteChange={(favorite) => {
            setPosts((currentPosts) =>
              feedSort === "favorites" && !favorite
                ? currentPosts.filter((post) => post.id !== item.id)
                : currentPosts.map((post) =>
                    post.id === item.id
                      ? { ...post, favorited: favorite }
                      : post,
                  ),
            );
          }}
          ref={(PostSingeRef) => (mediaRefs.current[item.id] = PostSingeRef)}
        />
      </View>
    );
  };

  return (
    <View
      style={[
        styles.container,
        Platform.OS === "web" && styles.mouseDragSurface,
      ]}
      onLayout={(event) => setFeedItemHeight(event.nativeEvent.layout.height)}
      {...gestureResponder.panHandlers}
    >
      {profile && (
        <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel="返回主页"
          style={styles.homeButton}
          onPress={() => navigateToHomeFeed(navigation)}
        >
          <Feather name="arrow-left" size={20} color="white" />
          <Text style={styles.homeButtonText}>返回主页</Text>
        </TouchableOpacity>
      )}
      {feedItemHeight > 0 && (
        <FlatList
          ref={flatListRef}
          data={posts}
          extraData={`${currentIndex}:${feedActive}`}
          windowSize={3}
          initialNumToRender={2}
          maxToRenderPerBatch={2}
          removeClippedSubviews={Platform.OS !== "web"}
          getItemLayout={(_data, index) => ({
            length: feedItemHeight,
            offset: feedItemHeight * index,
            index,
          })}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 80,
          }}
          renderItem={renderItem}
          pagingEnabled
          scrollEnabled
          nestedScrollEnabled
          directionalLockEnabled
          disableIntervalMomentum
          snapToInterval={feedItemHeight}
          snapToAlignment="start"
          keyExtractor={(item) => item.id}
          decelerationRate={"fast"}
          onViewableItemsChanged={onViewableItemsChanged.current}
          onScroll={(event: NativeSyntheticEvent<NativeScrollEvent>) => {
            if (Platform.OS === "web") {
              updateIndexFromScrollOffset(event.nativeEvent.contentOffset.y);
            }
          }}
          scrollEventThrottle={16}
          onMomentumScrollEnd={(
            event: NativeSyntheticEvent<NativeScrollEvent>,
          ) => {
            updateIndexFromScrollOffset(event.nativeEvent.contentOffset.y);
          }}
          onScrollToIndexFailed={({ index }) => {
            setTimeout(() => {
              flatListRef.current?.scrollToIndex({ index, animated: true });
            }, 250);
          }}
          showsVerticalScrollIndicator={false}
          ListEmptyComponent={
            <View style={[styles.emptyState, { height: feedItemHeight }]}>
              <Feather
                name={feedSort === "favorites" ? "bookmark" : "film"}
                size={34}
                color="rgba(255,255,255,0.72)"
              />
              <Text style={styles.emptyStateTitle}>
                {feedSort === "favorites" ? "还没有收藏的视频" : "暂无视频"}
              </Text>
              {feedSort === "favorites" && (
                <Text style={styles.emptyStateText}>
                  在视频右侧点“收藏”，内容会出现在这里
                </Text>
              )}
            </View>
          }
        />
      )}
    </View>
  );
}

export default function FeedScreen({ route }: { route: FeedScreenRouteProp }) {
  const [strategyIndex, setStrategyIndex] = useState(0);
  const [topChromeVisible, setTopChromeVisible] = useState(false);
  const routeParams = route.params as
    | { profile?: boolean; videos?: unknown[] }
    | undefined;
  const strategyEnabled = !routeParams?.profile && !routeParams?.videos;
  const handleStrategySwipe = useCallback((direction: "left" | "right") => {
    setStrategyIndex((currentIndex) =>
      direction === "left"
        ? (currentIndex + 1) % mobileFeedStrategies.length
        : (currentIndex - 1 + mobileFeedStrategies.length) %
          mobileFeedStrategies.length,
    );
  }, []);

  if (!strategyEnabled) {
    return <FeedList route={route} />;
  }

  const activeStrategy = mobileFeedStrategies[strategyIndex];

  return (
    <View style={styles.container}>
      <FeedList
        key={activeStrategy.key}
        route={route}
        feedSort={activeStrategy.key}
        onHorizontalSwipe={handleStrategySwipe}
        onChromeVisibilityChange={setTopChromeVisible}
      />

      {topChromeVisible && (
        <View style={styles.strategyTabs} accessibilityRole="tablist">
          {mobileFeedStrategies.map((strategy, index) => {
            const active = strategyIndex === index;
            return (
              <TouchableOpacity
                key={strategy.key}
                accessibilityRole="tab"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`播放策略：${strategy.group} ${strategy.label}`}
                style={[
                  styles.strategyTab,
                  active && styles.strategyTabActive,
                  active &&
                    strategy.key === "public" &&
                    styles.strategyTabPublicActive,
                ]}
                onPress={() => setStrategyIndex(index)}
              >
                <Text
                  style={[
                    styles.strategyGroup,
                    active && styles.strategyTextActive,
                  ]}
                >
                  {strategy.group}
                </Text>
                <Text
                  style={[
                    styles.strategyLabel,
                    active && styles.strategyTextActive,
                  ]}
                >
                  {strategy.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      )}
    </View>
  );
}
